import { getBvStatuses } from '@/components/Templates/BenefitVerification/BenefitVerificationTable/bv';
import type { BVRecord } from '@/types/benefitsVerification';
import { extractDocumentName, formatDate } from '@/utils';

const statuses = getBvStatuses();

export const transformBVRecordToPreview = (
	record: BVRecord | null,
	accountProducts: Array<{
		label: string;
		value: number;
		qCode: string;
	}> = [],
) => {

	const productsSelected = record?.ta_productselection && accountProducts?.length
			? accountProducts
					.map((item) => item.label)
					.join(', ')
			: record?.ta_productselection || '';
	const productStatuses = 
		record?.priorAuths && accountProducts?.length
			? accountProducts
					.map((product) => {
						const matchingAuth = record.priorAuths?.find(
							(auth) => auth.ta_name === product.label
						);
						if (!matchingAuth) return null;

						const statusKey = String(matchingAuth.ta_pa_status) as keyof typeof statuses;
						return `${product.label}: ${statuses[statusKey] ?? ''}`;
					})
					.filter(Boolean)
					.join(', ')
			: '';
	
	const calculateTotalWounds = () => {
		if (
			record?.woundThreeApplicationCode1 ||
			record?.woundThreeApplicationCode2 ||
			record?.woundThreeDiagnosisCode1 ||
			record?.woundThreeDiagnosisCode2 ||
			record?.woundThreeDiagnosisCode3 ||
			record?.woundThreeDiagnosisCode4 ||
			record?.ta_wound3_woundlength ||
			record?.ta_wound3_woundwidth ||
			record?.ta_wound3_wounddepth ||
			record?.ta_wound3_woundsizetotal ||
			record?.ta_woundtype3
		) {
			return 3;
		}
		if (
			record?.woundTwoApplicationCode1 ||
			record?.woundTwoApplicationCode2 ||
			record?.woundTwoDiagnosisCode1 ||
			record?.woundTwoDiagnosisCode2 ||
			record?.woundTwoDiagnosisCode3 ||
			record?.woundTwoDiagnosisCode4 ||
			record?.ta_wound2_woundlength ||
			record?.ta_wound2_woundwidth ||
			record?.ta_wound2_wounddepth ||
			record?.ta_wound2_woundsizetotal ||
			record?.ta_woundtype2
		) {
			return 2;
		}
		return 1;
	};

	return {
		clinicName: record?.account?.name || '',
		clinic: record?.account?.accountid || '',
		providerName: record?.provider?.fullname || '',
		patientName: record?.patient?.fullname || '',
		birthdate: record?.ta_dob || '',
		primaryInsurance: record?.primaryInsurance?.ta_name || '',
		secondaryInsurance: record?.secondaryInsurance?.ta_name || '',
		primaryInsuranceMemberID: record?.ta_primarymemberid || '',
		secondaryInsuranceMemberID: record?.ta_secondarymemberid || '',
		emrId: record?.ta_emrid || '',
		isPatientInNursingHome: record?.ta_nursinghome ? 'Yes' : 'No',
		hasPatientHad100DaysOfCare: record?.ta_100days ? 'Yes' : 'No',
		createdOn: record?.createdon ? formatDate(record?.createdon) : '-',
		productSelection: productsSelected,
		productStatuses: productStatuses,
		procedureDate: record?.ta_proceduredate || '',
		noteshartreview: record?.ta_noteshartreview || '',
		wounds: [
			{
				applicationCode1: record?.woundOneApplicationCode1?.ta_name || '',
				applicationCode2: record?.woundOneApplicationCode2?.ta_name || '',
				woundDepth: record?.ta_wounddepth || 0,
				woundLength: record?.ta_woundlength || 0,
				woundWidth: record?.ta_woundwidth || 0,
				woundType: record?.ta_woundtype1?.toString() || '',
				dCode1: record?.woundOneDiagnosisCode1?.ta_name || '',
				dCode2: record?.woundOneDiagnosisCode2?.ta_name || '',
				dCode3: record?.woundOneDiagnosisCode3?.ta_name || '',
				dCode4: record?.woundOneDiagnosisCode4?.ta_name || '',
			},
			{
				applicationCode1: record?.woundTwoApplicationCode1?.ta_name || '',
				applicationCode2: record?.woundTwoApplicationCode2?.ta_name || '',
				woundDepth: record?.ta_wound2_wounddepth || 0,
				woundLength: record?.ta_wound2_woundlength || 0,
				woundWidth: record?.ta_wound2_woundwidth || 0,
				woundType: record?.ta_woundtype2?.toString() || '',
				dCode1: record?.woundTwoDiagnosisCode1?.ta_name || '',
				dCode2: record?.woundTwoDiagnosisCode2?.ta_name || '',
				dCode3: record?.woundTwoDiagnosisCode3?.ta_name || '',
				dCode4: record?.woundTwoDiagnosisCode4?.ta_name || '',
			},
			{
				applicationCode1: record?.woundThreeApplicationCode1?.ta_name || '',
				applicationCode2: record?.woundThreeApplicationCode2?.ta_name || '',
				woundDepth: record?.ta_wound3_wounddepth || 0,
				woundLength: record?.ta_wound3_woundlength || 0,
				woundWidth: record?.ta_wound3_woundwidth || 0,
				woundType: record?.ta_woundtype3?.toString() || '',
				dCode1: record?.woundThreeDiagnosisCode1?.ta_name || '',
				dCode2: record?.woundThreeDiagnosisCode2?.ta_name || '',
				dCode3: record?.woundThreeDiagnosisCode3?.ta_name || '',
				dCode4: record?.woundThreeDiagnosisCode4?.ta_name || '',
			},
		],
		totalWoundsCreated: calculateTotalWounds(),
		portalBVUploadUrl: record?.ta_portalbvupload || '',
		uploadedFiles: record?.ta_portalbvupload
			? [extractDocumentName(record.ta_portalbvupload)]
			: [],
	};
};